import { useAtomValue } from "jotai";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { BsFillSuitHeartFill, BsSuitHeart } from "react-icons/bs";
import { GiCrossedSwords } from "react-icons/gi";
import { IoInformationCircleOutline, IoStar, IoStarOutline } from "react-icons/io5";
import { MdOutlineMoreHoriz } from "react-icons/md";

import { DeckVisibilityEnum } from "@sorcery/db";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  toast,
} from "@sorcery/ui";

import { ModalTypeEnum, useModal } from "~/providers/ModalProvider";
import { useUser } from "~/providers/UserProvider";
import { deckJotai } from "~/state/deckJotai";
import { DeckModeEnum } from "~/types/deck";
import { hasPrimer, isPrivate } from "~/utils/deck";
import { api } from "~/utils/trpc";

export const DeckActions = () => {
  const trpcUtils = api.useUtils();
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const deck = useAtomValue(deckJotai)!;
  const { query: routerParams, replace } = useRouter();
  const { user } = useUser();
  const { openModal } = useModal();

  const { mutate: starDeck, isPending: isStarring } = api.deck.toggleStar.useMutation({
    onSuccess: () => {
      toast(`Deck ${starred ? "unstarred" : "starred"}`);
      // todo add similar cache logic to user
      void trpcUtils.user.getBySession.invalidate();
    },
    onError: () => {
      toast.error("Something went wrong");
    },
  });
  const { mutate: likeDeck, isPending: isLiking } = api.deck.likeDeck.useMutation({
    onSuccess: () => {
      toast(`Deck ${liked ? "unliked" : "liked"}`);
      void trpcUtils.user.getBySession.invalidate();
      void trpcUtils.deck.getById.invalidate({ id: deck.id });
    },
    onError: () => {
      toast.error("Something went wrong");
    },
  });
  const { mutateAsync: updateSettings, isPending: isPublishing } =
    api.deck.updateSettings.useMutation({
      onSuccess: () => {
        toast("Deck updated");
        void trpcUtils.deck.getById.invalidate({ id: deck.id });
      },
      onError: () => {
        toast.error("Something went wrong");
      },
    });

  const starred = useMemo(
    () => !!user?.stars.find((f) => f.deckId === deck.id),
    [deck.id, user?.stars],
  );

  const liked = useMemo(
    () => !!user?.likes.find((l) => l.deckId === deck.id),
    [deck.id, user?.likes],
  );

  const isAuthor = useMemo(
    () => !!user && !!deck.user && user.id === deck.user.id,
    [deck.user, user],
  );

  return (
    <div className="flex flex-row flex-wrap items-center justify-between gap-2 py-2">
      <div className="flex flex-row items-center">
        {/* primer */}
        {(hasPrimer(deck.primer) || isAuthor) && (
          <button
            type="button"
            className="text-red mr-4 flex items-center sm:mr-6"
            onClick={() =>
              replace(
                {
                  query: { ...routerParams, tab: DeckModeEnum.PRIMER },
                },
                undefined,
                {
                  shallow: true,
                },
              )
            }
          >
            <GiCrossedSwords size={18} className="mr-1 inline" />

            <span className="whitespace-nowrap">
              <span className="hidden sm:inline">
                {isAuthor && !hasPrimer(deck.primer) ? "Create" : "Read"}
              </span>{" "}
              <span>Primer</span>
            </span>
          </button>
        )}

        {/* publish */}
        {isPrivate(deck.visibility) && isAuthor && (
          <button
            type="button"
            className={`mr-4 flex items-center sm:mr-6 ${isPublishing ? "opacity-50" : ""}`}
            onClick={() =>
              updateSettings({
                deckId: deck.id,
                visibility: DeckVisibilityEnum.Public,
              })
            }
            disabled={isPublishing}
          >
            <IoInformationCircleOutline size={18} className="-mt-[1px] mr-1 inline" />
            <span>Publish</span>
          </button>
        )}

        {/* star */}
        {isAuthor ? (
          <button
            type="button"
            className={`mr-4 flex items-center sm:mr-6 ${isStarring ? "opacity-50" : ""}`}
            onClick={() => starDeck({ id: deck.id })}
            disabled={isStarring}
          >
            {starred ? (
              <IoStar className={"mr-1 inline"} size={18} />
            ) : (
              <IoStarOutline className={"mr-1 inline"} size={18} />
            )}

            <span>{starred ? "Unstar" : "Star"}</span>
          </button>
        ) : (
          user && (
            // like
            <button
              type="button"
              className={`mr-4 flex items-center sm:mr-6 ${isLiking ? "opacity-50" : ""}`}
              onClick={() => likeDeck({ id: deck.id })}
              disabled={isLiking}
            >
              {liked ? (
                <BsFillSuitHeartFill className={"mr-1 inline"} size={15} />
              ) : (
                <BsSuitHeart className={"mr-1 inline"} size={15} />
              )}
              <span>{liked ? "Unlike" : "Like"}</span>
            </button>
          )
        )}

        {/*  more */}
        {user && (
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild className="hover:opacity-60">
              <Button variant="minimal">
                <MdOutlineMoreHoriz size={18} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="bottom" align="end">
              <DropdownMenuItem onClick={() => openModal(ModalTypeEnum.COPYDECK)}>
                Copy Deck
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => openModal(ModalTypeEnum.EXPORTDECK)}>
                Play in TTS
              </DropdownMenuItem>
              {isAuthor && (
                <DropdownMenuItem
                  onClick={() =>
                    replace(
                      {
                        query: { ...routerParams, tab: DeckModeEnum.SETTINGS },
                      },
                      undefined,
                      {
                        shallow: true,
                      },
                    )
                  }
                >
                  Settings
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
};
